import React, { useState } from 'react';
import { X, Mail, Lock, User as UserIcon, KeyRound, ArrowRight, Loader2, Gift } from 'lucide-react';
import { authApi, getStoredReferral, clearStoredReferral } from '../api';
import { useAuth } from '../AuthContext';
import { useToast } from '../hooks/use-toast';
import Turnstile from './Turnstile';

const AuthModal = ({ open, onClose, initialMode = 'login' }) => {
  const { finalizeLogin } = useAuth();
  const { toast } = useToast();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [devCode, setDevCode] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const referredBy = getStoredReferral();

  React.useEffect(() => { if (open) setMode(initialMode); }, [open, initialMode]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((mode === 'register' || mode === 'login') && process.env.REACT_APP_TURNSTILE_SITE_KEY && !captchaToken) {
      toast({ title: 'Please complete the captcha', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      if (mode === 'register') {
        const res = await authApi.register({ email, username, password, fullName, referredBy: referredBy || undefined, captchaToken });
        if (res.dev_code) setDevCode(res.dev_code);
        toast({ title: 'Account created', description: referredBy ? `Linked to your referrer @${referredBy}. Check your email for the code.` : 'Check your email for the verification code.' });
        setMode('verify');
      } else if (mode === 'login') {
        const res = await authApi.login({ email, password, captchaToken });
        if (res.requires_verification) {
          if (res.dev_code) setDevCode(res.dev_code);
          toast({ title: 'Verification needed', description: 'A new code was sent to your email.' });
          setMode('verify');
        } else {
          finalizeLogin(res);
          toast({ title: `Welcome back, ${res.user?.username}!` });
          onClose();
        }
      } else if (mode === 'verify') {
        const res = await authApi.verifyEmail({ email, code });
        finalizeLogin(res);
        clearStoredReferral();
        toast({ title: 'Email verified', description: 'Welcome to Domino Arena!' });
        onClose();
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Something went wrong';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      const res = await authApi.resendCode(email);
      if (res.dev_code) setDevCode(res.dev_code);
      toast({ title: 'Code resent', description: 'Check your email.' });
    } catch (e) {
      toast({ title: 'Failed to resend', variant: 'destructive' });
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[92%] max-w-[440px] bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="text-xl font-black text-slate-900">
            {mode === 'login' ? 'Sign In' : mode === 'register' ? 'Create Account' : 'Verify Email'}
          </h2>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
            <X className="w-4 h-4 text-slate-700" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-3">
          {mode !== 'verify' && (
            <div>
              <label className="text-[11px] font-bold tracking-[0.14em] text-slate-500">{mode === 'register' ? 'GMAIL ADDRESS' : 'EMAIL'}</label>
              <div className="mt-1.5 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-3 py-3 text-[14px] font-semibold text-slate-900 focus:outline-none focus:border-blue-400" placeholder={mode === 'register' ? 'yourname@gmail.com' : 'you@example.com'} />
              </div>
            </div>
          )}

          {mode === 'register' && referredBy && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl px-3 py-2 text-[12px]" data-testid="referral-banner">
              <Gift className="w-4 h-4 shrink-0" />
              <span>Vous vous inscrivez via <b>@{referredBy}</b> — votre parrain recevra une commission DOMIA à chaque dépôt.</span>
            </div>
          )}

          {mode === 'register' && (
            <div>
              <label className="text-[11px] font-bold tracking-[0.14em] text-slate-500">FULL NAME</label>
              <div className="mt-1.5 relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  required
                  minLength={2}
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-3 py-3 text-[14px] font-semibold text-slate-900 focus:outline-none focus:border-blue-400"
                  placeholder="John Doe"
                  data-testid="full-name-input"
                />
              </div>
            </div>
          )}

          {mode === 'register' && (
            <div>
              <label className="text-[11px] font-bold tracking-[0.14em] text-slate-500">USERNAME</label>
              <div className="mt-1.5 relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="text" required minLength={3} value={username} onChange={e => setUsername(e.target.value)} className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-3 py-3 text-[14px] font-semibold text-slate-900 focus:outline-none focus:border-blue-400" placeholder="CryptoKing" data-testid="username-input" />
              </div>
            </div>
          )}

          {mode !== 'verify' && (
            <div>
              <label className="text-[11px] font-bold tracking-[0.14em] text-slate-500">PASSWORD</label>
              <div className="mt-1.5 relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="password" required minLength={6} value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-3 py-3 text-[14px] font-semibold text-slate-900 focus:outline-none focus:border-blue-400" placeholder="••••••" />
              </div>
            </div>
          )}

          {mode === 'verify' && (
            <>
              <div className="text-[13px] text-slate-600 mb-1">We sent a 6-digit code to <span className="font-bold text-slate-900">{email}</span></div>
              <div>
                <label className="text-[11px] font-bold tracking-[0.14em] text-slate-500">VERIFICATION CODE</label>
                <div className="mt-1.5 relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input type="text" inputMode="numeric" required maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/[^0-9]/g, ''))} className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-3 py-3 text-[18px] font-black tracking-[0.4em] text-slate-900 focus:outline-none focus:border-blue-400" placeholder="000000" />
                </div>
              </div>
              {devCode && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <strong>DEV mode:</strong> code is <code className="font-black">{devCode}</code>
                </div>
              )}
              <button type="button" onClick={resend} className="text-[12px] font-bold text-blue-600 hover:text-blue-700">Resend code</button>
            </>
          )}

          {(mode === 'login' || mode === 'register') && (
            <div className="mt-1"><Turnstile onToken={setCaptchaToken} action={mode} /></div>
          )}

          <button type="submit" disabled={loading} className="w-full rounded-full py-3.5 font-extrabold text-white text-[14px] mt-2 flex items-center justify-center gap-2 shadow-[0_10px_24px_rgba(37,99,235,0.45)] disabled:opacity-60" style={{ background: 'linear-gradient(90deg, #1d4ed8, #3b82f6)' }}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {mode === 'login' ? 'Sign In' : mode === 'register' ? 'Create Account' : 'Verify'}
          </button>

          <div className="text-center text-[13px] text-slate-600 pt-1">
            {mode === 'login' && (
              <>No account? <button type="button" onClick={() => { setMode('register'); setDevCode(null); }} className="font-extrabold text-blue-600">Create one</button></>
            )}
            {mode === 'register' && (
              <>Already have an account? <button type="button" onClick={() => { setMode('login'); setDevCode(null); }} className="font-extrabold text-blue-600">Sign in</button></>
            )}
            {mode === 'verify' && (
              <button type="button" onClick={() => { setMode('login'); setDevCode(null); }} className="font-extrabold text-blue-600">Back to sign in</button>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default AuthModal;
